import { Theme } from "@material-ui/core";
import Drawer from "@material-ui/core/Drawer";
import AttachmentIcon from "@material-ui/icons/Attachment";
import CloseIcon from "@material-ui/icons/CloseOutlined";
import ImageIcon from "@material-ui/icons/Image";
import VisibilityIcon from "@material-ui/icons/Visibility";
import * as registry from "@qpr/client-api-registry";
import React, { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import useAttachments from "./../../apihooks/useAttachments";
import { useAppContext } from "./../../context/AppCtxProvider";
import useDownload from "./../../utils/useDownload";
import { makeThemedStyles } from "./../../viewcomponents/utils";
import { FileValue } from "./FileValue";
const useStyles = makeThemedStyles(
  "MilestoneInput.UploadedFileList",
  (theme: Theme) => ({
    uploadedDocuments: {
      display: "flex",
      flexWrap: "wrap",
    },
    documentDownload: {
      position: "relative",
      marginRight: "0.3em",
      padding: "0.5em",
      color: "#4C5157",
      borderStyle: "solid",
      borderColor: "rgb(232,234,237)",
      borderRadius: "8px",
      borderWidth: "1px",
      textAlign: "center",
      fontSize: "1.5em",
      minWidth: "200px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      background: "rgb(250,250,250)",
      justifyContent: "space-between",
      "&:last-child": {
        marginRight: 0,
      },
    },
    visibilityIcon: {
      width: "20px",
      height: "20px",
      marginLeft: "0.8em",
      paddingLeft: "0.3em",
      verticalAlign: "middle",
      borderLeft: "1px solid #D1E2EA",
      color: "#8FB8CC",
    },
    closeIcon: {
      width: "20px",
      height: "20px",
      paddingLeft: "0.3em",
      verticalAlign: "middle",
      color: "#8FB8CC",
    },
    fileIcon: {
      width: "1.8em",
      height: "1.8em",
      color: "rgb(241, 86, 66)",
      marginRight: "0.5em",
      verticalAlign: "middle",
    },
    drawer: {
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      padding: "0 1em 1em 1em",
    },
    collapseButton: {
      color: "rgb(0, 169, 163)",
      minWidth: "48px",
      height: "42.13px",
    },
    collapseIcon: {
      color: "rgb(17, 94, 103)",
      marginRight: "4.08px",
      width: "10.82px",
      height: "11.91px",
    },
  })
);
type IFileProps = {
  type: string;
  className?: string;
};
type IFileCard = {
  index: number;
  file: FileValue;
  handleFilePreview: (file: FileValue) => void;
  handleFileDelete: (id: string) => void;
  readOnly: boolean;
};
type ICollapseButton = {
  items: number;
  onClick: () => void;
};
function FileIcon(props: IFileProps) {
  const PdfIcon = registry.getSvg("PdfIcon");
  const { type, className } = props;
  if (type.includes("image")) return <ImageIcon className={className} />;
  if (type.includes("pdf")) return <PdfIcon className={className} />;
  return <AttachmentIcon className={className} />;
}
interface DeleteFileProps {
  file: {
    id: string;
  };
  onSuccess: () => void;
}
const DeleteFileButton = ({ file, onSuccess }: DeleteFileProps) => {
  const attachmentsApi = useAttachments();
  const classes = useStyles();
  const queryClient = useQueryClient();
  const ctx = useAppContext();
  const { mutate } = attachmentsApi.useDelete(file.id, {
    onSuccess: async () => {
      onSuccess();
      const params = new URLSearchParams({
        period: ctx.period || "latest",
        parentId: ctx.elementId || "null",
      }).toString();
      await queryClient.invalidateQueries(`attachments-?${params}`);
    },
  });
  const handleFileDelete = () => {
    mutate();
  };
  return (
    <CloseIcon
      className={classes.closeIcon}
      onClick={() => handleFileDelete()}
    />
  );
};
function FileCard(props: IFileCard) {
  const { file, handleFilePreview, handleFileDelete, readOnly } = props;
  const classes = useStyles();
  return (
    <div className={classes.documentDownload}>
      <FileIcon type={file.type} className={classes.fileIcon} />
      {file.name}
      <div>
        <VisibilityIcon
          className={classes.visibilityIcon}
          onClick={() => handleFilePreview(file)}
        />
        {!readOnly && (
          <DeleteFileButton
            file={file}
            onSuccess={() => handleFileDelete(file.id)}
          />
        )}
      </div>
    </div>
  );
}
function CollapseButton(props: ICollapseButton) {
  const { items, onClick } = props;
  const classes = useStyles();
  const AttachmentPinIcon = registry.getSvg("AttachmentPinIcon");
  return (
    <button
      className={`${classes.documentDownload} ${classes.collapseButton}`}
      type="button"
      onClick={onClick}
    >
      <AttachmentPinIcon className={classes.collapseIcon} /> +{items}
    </button>
  );
}
interface Props {
  values?: FileValue[];
  files?: File[];
  collapsible?: boolean;
  collapseItemsAfter?: number;
  handleFileRemove?: (index: number) => void;
  readOnly?: boolean;
}
/**
 *
 * @param props {string[]} values File preview urls
 * @param props {File[]} files Files for FileUploadInput.tsx
 * @param props{(index: number)=>void} handleFileRemove Callback for remove files(FileUploadInput.tsx)
 * @returns
 */
export default function UploadedFilesList(props: Props) {
  const { collapsible, collapseItemsAfter, readOnly } = props;
  const [values, setValues] = useState<FileValue[]>([]);
  const [defaultValues, setDefaultValues] = useState<FileValue[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [drawer, setDrawer] = useState(false);
  const [drawerItems, setDrawerItems] = useState<FileValue[]>([]);
  const download = useDownload();
  const classes = useStyles();
  const appCtx = useAppContext();
  const getFileData = (file: FileValue): FileValue => {
    let type = "image";
    const ext = file.name
      .substr(file.name.lastIndexOf(".") + 1)
      .toLocaleLowerCase();
    if (["jpg", "jpeg", "svg", "ico", "gif", "png"].includes(ext))
      type = "image";
    else if (ext === "pdf") type = "pdf";
    else type = ext;
    return {
      ...file,
      type,
      url: `${appCtx.serverURL}/api/attachments/${file.id}`,
    };
  };
  const handleFileDelete = (id: string) => {
    setDefaultValues((values) => values.filter((value) => value.id !== id));
  };
  const handleFileRemove = (fileIndex: number) => {
    if (props.handleFileRemove) props.handleFileRemove(fileIndex);
  };
  const handleFilePreview = (file: File | FileValue) => {
    if (file instanceof File) {
      const fileName = file.name;
      const dataUrl = URL.createObjectURL(file);
      download(dataUrl, fileName);
    } else {
      window.location.replace(file.url);
    }
  };
  useEffect(() => {
    setDefaultValues(props.values?.map(getFileData) || []);
  }, [appCtx.elementId, appCtx.period, props.values?.length]);
  useEffect(() => {
    setValues(() => {
      if (collapsible && collapseItemsAfter) {
        return defaultValues.slice(0, collapseItemsAfter);
      }
      return defaultValues;
    });
    setDrawerItems(
      collapseItemsAfter ? defaultValues.slice(collapseItemsAfter) : []
    );
  }, [
    defaultValues.map(({ id }) => id).join(),
    appCtx.elementId,
    appCtx.period,
  ]);
  useEffect(() => {
    setFiles(props.files || []);
  }, [props.files, props.files?.length]);
  return (
    <>
      <div className={classes.uploadedDocuments}>
        {values.map((file, index) => (
          <FileCard
            readOnly={!!readOnly}
            key={file.id}
            file={file}
            index={index}
            handleFileDelete={handleFileDelete}
            handleFilePreview={handleFilePreview}
          />
        ))}
        {files.map((file, index) => (
          <div key={index} className={classes.documentDownload}>
            <FileIcon type={file.type} className={classes.fileIcon} />
            {file.name}
            <div>
              <VisibilityIcon
                className={classes.visibilityIcon}
                onClick={() => handleFilePreview(file)}
              />
              <CloseIcon
                className={classes.closeIcon}
                onClick={() => handleFileRemove(index)}
              />
            </div>
          </div>
        ))}
        {!!collapseItemsAfter && collapsible && !!drawerItems.length && (
          <CollapseButton
            items={drawerItems.length}
            onClick={() => setDrawer(true)}
          />
        )}
      </div>
      {!!collapseItemsAfter && !!drawerItems.length && collapsible && (
        <Drawer
          anchor="bottom"
          open={drawer}
          onClose={() => setDrawer(false)}
          classes={{ paper: classes.drawer }}
        >
          {drawerItems.map((file, index) => (
            <FileCard
              readOnly={!!readOnly}
              file={file}
              key={file.id}
              index={index + collapseItemsAfter}
              handleFileDelete={handleFileDelete}
              handleFilePreview={handleFilePreview}
            />
          ))}
        </Drawer>
      )}
    </>
  );
}
